export const CHANGE_WINDOW_DAYS = 45;
const DAY_MS = 86400000;

// Exact full names verified against CaoBuilding and existing index labels.
// Avoid substring matches or generic legal-suffix stripping across distinct firms.
export const KEY_COMPANIES = [
  { name: 'DEL', aliases: ['Del Property Management Inc.'] },
  { name: 'Andrejs', aliases: ['Andrejs Management Incorporated'] },
  { name: 'City Sites', aliases: ['City Sites Property Management Inc.'] },
  { name: 'Duka', aliases: ['Duka Property Management Inc.'] },
  { name: 'Kindle', aliases: ['Kindle Management Inc.'] },
];
const text = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');
const key = (value) => text(value).toLowerCase();
const aliases = new Map(KEY_COMPANIES.flatMap((company) =>
  [company.name, ...company.aliases].map((name) => [key(name).replace(/\./g, ''), company.name])));
const UNKNOWN = /^(?:not listed|unknown|n\/?a|licensed condo management company could not be confirmed at this time)$/i;

export function companyIdentity(value) {
  const name = text(value);
  if (!name || UNKNOWN.test(name)) return null;
  const label = aliases.get(key(name).replace(/\./g, '')) || name;
  return { key: key(label), label };
}

export function changeTime(value) {
  if (typeof value === 'number') return value;
  // The legacy refresher timestamp needs normalization for Safari.
  const normalized = String(value || '').replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) UTC$/, '$1T$2Z');
  return value ? new Date(normalized).getTime() : NaN;
}

export function inChangeWindow(value, end = Date.now()) {
  const time = changeTime(value);
  return Number.isFinite(time) && time >= end - CHANGE_WINDOW_DAYS * DAY_MS && time <= end;
}

export function inWindow(value, start, end) {
  const time = changeTime(value);
  return Number.isFinite(time) && time >= start && time <= end;
}

export function comparisonWindow(report, mode, now = Date.now()) {
  if (mode === 'history') return {start: now - CHANGE_WINDOW_DAYS * DAY_MS, end: now};
  const latest = report?.latest;
  return {start: changeTime(latest?.startedAt), end: changeTime(latest?.finishedAt),
    runId: latest?.id, baselineRunId: report?.baseline?.id};
}

export async function loadCompanyHistory(api, token, end, signal, options = {}) {
  const rows = [];
  const since = new Date(options.start ?? end - CHANGE_WINDOW_DAYS * DAY_MS).toISOString();
  const until = new Date(end).toISOString();
  let skip = 0;
  for (;;) {
    const params = new URLSearchParams({ field: 'company', since, until, limit: '500', skip: String(skip) });
    if (options.runId) params.set('runId', options.runId);
    const response = await fetch(`${api}/api/discovery/changes?${params}`, {
      headers: { Authorization: `Bearer ${token}` }, signal,
    });
    if (!response.ok) throw new Error('Company history unavailable');
    const data = await response.json();
    // An older server returns capped data without completeness metadata.
    if (!Array.isArray(data.changes) || typeof data.hasMore !== 'boolean') throw new Error('Incomplete history response');
    rows.push(...data.changes);
    if (!data.hasMore) return rows;
    if (!data.changes.length || data.nextSkip !== skip + data.changes.length) throw new Error('Invalid history page');
    skip = data.nextSkip;
  }
}

export function summarizeCompanyChanges(buildings, history, end, options = {}) {
  const start = options.start ?? end - CHANGE_WINDOW_DAYS * DAY_MS;
  // The same collapsed, filtered building cohort as the table, before paging.
  const byGuid = new Map(buildings.filter((row) => row.guid).map((row) => [row.guid, row]));
  const groups = new Map(KEY_COMPANIES.map(({ name }) => [key(name), {
    key: key(name), label: name, gains: 0, losses: 0, events: [],
  }]));
  const unresolved = [];
  const seenLatest = new Set();
  const lastEdge = new Map();
  const events = history.filter((row) => row.field === 'company' && byGuid.has(row.guid) && inWindow(row.at, start, end)
    && (!options.runId || (row.runId === options.runId && row.baselineRunId === options.baselineRunId)))
    .slice().sort((a, b) => changeTime(a.at) - changeTime(b.at));
  const getGroup = (identity) => {
    if (!groups.has(identity.key)) groups.set(identity.key, { ...identity, gains: 0, losses: 0, events: [] });
    return groups.get(identity.key);
  };
  let confirmed = 0;
  events.forEach((row) => {
    const oldCompany = companyIdentity(row.was);
    const newCompany = companyIdentity(row.now);
    const edge = JSON.stringify([oldCompany?.key ?? key(row.was), newCompany?.key ?? key(row.now)]);
    seenLatest.add(JSON.stringify([row.guid, edge]));
    // Repeated writes can differ by seconds. A return transfer breaks the run.
    if (lastEdge.get(row.guid) === edge) return;
    lastEdge.set(row.guid, edge);
    if (oldCompany && newCompany && oldCompany.key === newCompany.key) return;
    const event = { ...row, building: byGuid.get(row.guid), oldCompany, newCompany };
    if (row.initialImport || row.baselineEligible === false || (options.runId && row.baselineEligible !== true)) {
      unresolved.push({...event, reason: 'Initial import / preceding baseline not verified'});
      return;
    }
    if (!oldCompany || !newCompany) {
      unresolved.push({ ...event, reason: !oldCompany ? 'Previous company unknown' : 'New company unconfirmed' });
      return;
    }
    confirmed += 1;
    const gain = getGroup(newCompany);
    gain.gains += 1;
    gain.events.push({ ...event, direction: 'gain' });
    const loss = getGroup(oldCompany);
    loss.losses += 1;
    loss.events.push({ ...event, direction: 'loss' });
  });
  // A row can save successfully even if its append-only log write fails.
  buildings.forEach((building) => {
    if (!String(building.changedFields || '').split(',').map((s) => s.trim()).includes('company') || !inWindow(building.changedAt, start, end)) return;
    const was = building.changedFrom?.company;
    const edge = JSON.stringify([companyIdentity(was)?.key ?? key(was), companyIdentity(building.company)?.key ?? key(building.company)]);
    if (!seenLatest.has(JSON.stringify([building.guid, edge]))) unresolved.push({
      guid: building.guid, building, was, now: building.company, at: building.changedAt,
      reason: 'Change-history entry missing',
    });
  });
  return {
    confirmed,
    unresolved: unresolved.reverse(),
    companies: [...groups.values()].map((group) => ({
      ...group,
      net: group.gains - group.losses, events: group.events.reverse(),
    })),
  };
}
